import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Image } from "../common/ObjectTypes";
import { PrevArrow, NextArrow } from "../common/ProjectIcons";
import type { DetailBox } from "./mediaDetailComposer";
import ReviewedBadge, { REVIEWED_IMAGE_FILTER } from "./ReviewedBadge";

type ReviewQueueStripProps = {
  images: Image[];
  currentIndex: number;
  position: number; // 1-based position in the overall filtered set
  total: number;
  canStepPrev: boolean;
  canStepNext: boolean;
  onSelect: (index: number) => void;
  onStep: (direction: -1 | 1) => void;
};

// Thumbnail size is fixed by the tailwind classes on the strip button
// (h-16 w-24). The FilmstripThumbnail uses these px values to letterbox the
// image with object-contain and overlay boxes in the same coordinate space; if
// the button size ever changes, update these constants too.
const THUMB_WIDTH_PX = 96;
const THUMB_HEIGHT_PX = 64;

// AKL-782: matches BoundingBoxOverlay's fallback for a box that arrived without
// a colour. `DetailBox.color` is a required string, so this is unreachable via
// the composer — it is here so a malformed box looks the same broken red on the
// strip as it does on the main overlay, rather than passing as unactioned gold.
const MISSING_BOX_COLOR = "#FF0000";

/**
 * Filmstrip thumbnail with a decorative bounding-box overlay (AKL-681).
 *
 * Renders the photo with `object-contain` inside a fixed 96x64 box so the whole
 * image is visible (letterboxed if aspect ratios differ), then draws each box
 * as a thin 1px border rectangle in the same coordinate space.
 *
 * AKL-782: each rectangle is drawn in **its own** `box.color` — the review-state
 * colour the shared composer assigned it — so an accepted box is green here the
 * moment it is green on the main overlay. Until this ticket the strip applied
 * one hardcoded gold to every rectangle, a leftover from when it drew
 * colourless feed `Detection[]`, so a committed box stayed gold on the strip
 * for good. Do not reintroduce a local colour constant: the strip's job is to
 * agree with the overlay, not to have a palette.
 *
 * AKL-731: `boxes` is the same composed `DetailBox[]` the grid renders (sourced
 * from `Image.boxes`, via the shared `composeDetailBoxes`) instead of the old
 * feed-only `Detection[]`, so the film strip agrees with the grid and
 * full-screen on which boxes exist for a photo. One client-side filter remains
 * (see the render below): a rejected box travels in `image.boxes` for the
 * full-screen reveal and is dropped here unconditionally, because the strip
 * has no card to reveal it with. AKL-717: an excluded-with-no-fallback
 * detection is no longer absent; it arrives as an `unresolved` suggestion and
 * the strip draws its outline like any other box.
 *
 * Boxes are `pointer-events: none` so the parent <button>'s click still opens
 * the photo. No labels, hover, click, or ResizeObserver — this is a decorative
 * preview intended to stay cheap to render for 25+ thumbs on the strip; `label`/
 * `confidence` on `DetailBox` are simply unused here (per spec OQ-4, kept
 * outline-only for this ticket).
 *
 * @param url the thumbnail image URL
 * @param boxes composed boxes in image-pixel coordinates (may be empty)
 * @param dimmed when true, dims/desaturates the image (AKL-696) to mark it reviewed
 */
const FilmstripThumbnail: React.FC<{
  url: string;
  boxes: DetailBox[];
  dimmed?: boolean;
}> = ({ url, boxes, dimmed = false }) => {
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  // Letterbox rectangle inside the 96x64 container. Computed only when the
  // image's natural dimensions are known — before that, boxes are suppressed so
  // they don't overshoot the image while it loads.
  const rect = (() => {
    if (!natural || natural.w <= 0 || natural.h <= 0) return null;
    const imageAspect = natural.w / natural.h;
    const containerAspect = THUMB_WIDTH_PX / THUMB_HEIGHT_PX;
    let drawW = THUMB_WIDTH_PX;
    let drawH = THUMB_HEIGHT_PX;
    let offsetX = 0;
    let offsetY = 0;
    if (imageAspect > containerAspect) {
      drawH = THUMB_WIDTH_PX / imageAspect;
      offsetY = (THUMB_HEIGHT_PX - drawH) / 2;
    } else {
      drawW = THUMB_HEIGHT_PX * imageAspect;
      offsetX = (THUMB_WIDTH_PX - drawW) / 2;
    }
    return { drawW, drawH, offsetX, offsetY };
  })();

  return (
    <>
      <img
        src={url}
        alt=""
        data-testid="filmstrip-thumb-image"
        loading="lazy"
        draggable={false}
        onLoad={(e) => {
          const img = e.currentTarget;
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            setNatural({ w: img.naturalWidth, h: img.naturalHeight });
          }
        }}
        className="h-full w-full object-contain"
        style={dimmed ? { filter: REVIEWED_IMAGE_FILTER } : undefined}
      />
      {rect && boxes.length > 0 && (
        <div
          className="pointer-events-none absolute inset-0"
          data-testid="filmstrip-thumb-boxes"
        >
          {boxes.map((box, i) => {
            const left = rect.offsetX + (box.x / natural!.w) * rect.drawW;
            const top = rect.offsetY + (box.y / natural!.h) * rect.drawH;
            const width = (box.width / natural!.w) * rect.drawW;
            const height = (box.height / natural!.h) * rect.drawH;
            return (
              <div
                key={box.id ?? i}
                data-testid="filmstrip-thumb-box"
                style={{
                  position: "absolute",
                  left,
                  top,
                  width,
                  height,
                  border: `1px solid ${box.color || MISSING_BOX_COLOR}`,
                  boxSizing: "border-box",
                }}
              />
            );
          })}
        </div>
      )}
    </>
  );
};

/**
 * Horizontal thumbnail filmstrip of the current review-queue page (§6.2.1),
 * matching the Phase 2 mockup: the active photo is highlighted, thumbnails are
 * clickable, each thumb overlays its AI detection boxes (AKL-681), and prev/next
 * arrows plus an "N of M" readout let the reviewer walk the working set.
 * Reviewed thumbnails (AKL-696) carry a green check badge and a dimmed image,
 * sourced from `Image.review_status` on the feed payload.
 */
const ReviewQueueStrip: React.FC<ReviewQueueStripProps> = ({
  images,
  currentIndex,
  position,
  total,
  canStepPrev,
  canStepNext,
  onSelect,
  onStep,
}) => {
  const { t } = useTranslation();
  const activeRef = useRef<HTMLButtonElement>(null);

  // Keep the active thumbnail scrolled into view as the reviewer steps.
  useEffect(() => {
    activeRef.current?.scrollIntoView({
      inline: "center",
      block: "nearest",
    });
  }, [currentIndex]);

  return (
    <div className="flex items-center gap-3 border-t border-black/10 px-4 py-2 dark:border-white/10">
      <div className="flex shrink-0 flex-col">
        <span className="text-xs font-semibold uppercase tracking-wide text-off-black-light/80 dark:text-primary-off-white/80">
          {t("Review queue")}
        </span>
        <span className="text-[11px] text-off-black-light/60 dark:text-primary-off-white/50">
          {t("{{position}} of {{total}}", { position, total })}
        </span>
      </div>

      <div className="flex flex-1 gap-2 overflow-x-auto py-1">
        {images.map((image, index) => {
          const isActive = index === currentIndex;
          const isReviewed = image.review_status === "reviewed";
          return (
            <button
              key={image.id || image.url}
              ref={isActive ? activeRef : undefined}
              type="button"
              onClick={() => onSelect(index)}
              aria-label={
                isReviewed
                  ? t("Open photo {{n}} (reviewed)", { n: index + 1 })
                  : t("Open photo {{n}}", { n: index + 1 })
              }
              aria-current={isActive}
              className={`relative h-16 w-24 shrink-0 overflow-hidden rounded border transition-colors focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none ${
                isActive
                  ? "border-storm-blue ring-2 ring-storm-blue dark:border-sky-blue-light dark:ring-sky-blue-light"
                  : "border-black/10 hover:border-black/40 dark:border-white/10 dark:hover:border-white/40"
              }`}
            >
              <FilmstripThumbnail
                url={image.url}
                // AKL-731 (reject-reveal follow-up): the strip has no card to
                // click/hover, so a rejected box (present in `image.boxes`
                // for the full-screen reveal, see mediaDetailComposer.tsx)
                // must never show here, unconditionally.
                boxes={(image.boxes ?? []).filter(
                  (b) => b.visibility !== "rejected",
                )}
                dimmed={isReviewed}
              />
              {isReviewed && <ReviewedBadge ariaHidden size={16} />}
            </button>
          );
        })}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => onStep(-1)}
          disabled={!canStepPrev}
          aria-label={t("Previous photo")}
          className="flex h-8 w-8 items-center justify-center rounded bg-black/40 text-white/90 hover:bg-black/70 disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none"
        >
          <PrevArrow />
        </button>
        <button
          type="button"
          onClick={() => onStep(1)}
          disabled={!canStepNext}
          aria-label={t("Next photo")}
          className="flex h-8 w-8 items-center justify-center rounded bg-black/40 text-white/90 hover:bg-black/70 disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none"
        >
          <NextArrow />
        </button>
      </div>
    </div>
  );
};

export default ReviewQueueStrip;
